import { DatePipe } from '@angular/common';
import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AppConfig } from "../model/config";
import { UserInfo } from '../model/userInfo';
import { SettingsService } from './settings.service';
import { SignalRService } from "./signalr.service";
import { WorkShiftService } from './workShift.service';
import { UserMappingService } from './usermapping.service';
import { LocationService } from './location.service';
import { ActivityActorService } from './activityActor.service';
import { AuthService } from './auth.service';
import { MessageService } from './message.service';
import { LiveFlightService } from './liveFlight.service';
import { CustomError } from '../model/Errors/customErrors';
import { UserImportLogService } from './userimportlog.service';

@UntilDestroy()
@Injectable()
export class StartupService {

  private filterFrom: Date;
  private filterTo: Date;
  private filterShortDate: string;
  private todayShortDate: string;

  public login$: EventEmitter<UserInfo>;
  public logout$: EventEmitter<UserInfo>;
  public initCompleted$: EventEmitter<boolean>;

  public isLoggedIn: boolean = false;

  constructor(
    private readonly zone: NgZone,
    private readonly settings: SettingsService,
    private readonly signalRService: SignalRService,
    private readonly configuration: AppConfig,
    private readonly workShiftService: WorkShiftService,
    private readonly usermappingService: UserMappingService,
    private readonly locationService: LocationService,
    private readonly activityActorService: ActivityActorService,
    private readonly authService: AuthService,
    private readonly messageService: MessageService,
    private readonly liveFlightService: LiveFlightService,
    private readonly userImportLogService: UserImportLogService) {

    this.login$ = new EventEmitter<UserInfo>();
    this.logout$ = new EventEmitter<UserInfo>();
    this.initCompleted$ = new EventEmitter<boolean>();

    this.signalRService.reconnectedEvent$.subscribe((reconnected: boolean) => { this.onSignalRReconnected(); });

    this.signalRService.login$.subscribe((data: UserInfo) => { this.onLogin(data); });
    this.signalRService.logout$.subscribe((data: UserInfo) => { this.onLogout(data); });

    this.authService.authReady$.subscribe((ready) => {
      this.isLoggedIn = true;
    });

    this.authService.groupsReady$.subscribe(() => {

      if (authService.haveAnyAccessToPlanner()) {
        this.initService();
        return;
      }
      
      this.initCompleted$.emit(true);

    });

  }

  private async initService() {

    this.filterFrom = this.settings.windowStart;
    this.filterTo = this.settings.windowEnd;

    const pipe = new DatePipe(this.settings.getCurrentLocale());
    this.filterShortDate = pipe.transform(this.filterFrom, "yyyyMMdd");
    this.todayShortDate = pipe.transform(this.filterFrom, "yyyyMMdd");

    console.log("CONFIGURATION\n" +
      "CONF: filter from: " + this.filterFrom + "\n" +
      "CONF: filter to: " + this.filterTo + "\n" +
      "CONF: filter shortDate: " + this.filterShortDate + "\n" +
      "CONF: filter: todayShortDate: " + this.todayShortDate + "\n" +
      "CONF: backend url: " + this.configuration.backendUrl + "\n"
    );

    this.isLoggedIn = true;

    try {
      this.initSignalR();
            
      await this.locationService.loadLocations();

      await this.loadActorsActivitiesAndLiveFlights();

      // availabilities
      await this.workShiftService.initService();

      //usermappings
      await this.usermappingService.getAllUserMappings();

      await this.messageService.loadMessages();

      this.userImportLogService.init();

      

    } catch (error) {
      if (error instanceof CustomError) {
        // supress custom errors
      }
    }
    this.initCompleted$.emit(true);
  }

  private async loadActorsActivitiesAndLiveFlights() {
    await this.liveFlightService.initService();
    await this.activityActorService.initService();

  }

  private onLogin(data: UserInfo) {
    this.zone.run(() => {
      console.log('Login: ' + data.displayName);
      this.login$.emit(data)
    });
  }

  private onLogout(data: UserInfo) {
    this.zone.run(() => {
      console.log('Logout: ' + data.displayName);
      this.logout$.emit(data)
    });
  }

  private initSignalR(): void {
    this.signalRService.start(this.settings.getCurrentAirport());
  }

  private onSignalRReconnected() {
    this.loadActorsActivitiesAndLiveFlights();
    this.workShiftService.initService();
  }
}
